import { useEffect, useState, type ImgHTMLAttributes } from 'react';

import { cn } from '@/shared/lib/cn';

type LogoVariant = 'full' | 'mark';
type LogoTheme = 'light' | 'dark';

interface Props extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  /** `full` shows wordmark; `mark` shows just the leaf icon. */
  variant?: LogoVariant;
  /** `light` for dark backgrounds (sidebar); `dark` for light backgrounds (login). */
  theme?: LogoTheme;
  /** Visual height in px; width is auto. */
  height?: number;
}

/**
 * Brand logo sources.
 *
 * The Latin wordmark is the brand mark in every language — the logo no longer
 * swaps to a calligraphic Arabic version when the UI is in Arabic.
 */
const SOURCES: Record<LogoVariant, Record<LogoTheme, string>> = {
  full: {
    dark: '/brand/tarawud-logo.png',
    light: '/brand/tarawud-logo-white.png',
  },
  mark: {
    dark: '/brand/tarawud-mark.png',
    light: '/brand/tarawud-mark.png',
  },
};

/**
 * Brand logo for Tarawud POS. Falls back to a text mark if the PNG file isn't
 * present yet (drop the file in `public/brand/` to override).
 *
 *   <Logo variant="full" theme="dark" height={36} />   on a light surface
 *   <Logo variant="mark" theme="light" height={32} />  on a dark surface
 */
export function Logo({
  variant = 'full',
  theme = 'dark',
  height = 36,
  className,
  alt = 'Tarawud',
  ...rest
}: Props) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  const src = SOURCES[variant][theme];

  // Reset the failure state when the source changes (e.g. theme switch) so the
  // new image gets a fresh attempt even if the previous one failed.
  useEffect(() => {
    if (failedSrc !== null && failedSrc !== src) setFailedSrc(null);
  }, [src, failedSrc]);

  if (failedSrc === src) {
    return (
      <span
        className={cn(
          'inline-flex select-none items-center font-bold tracking-tight',
          theme === 'light' ? 'text-white' : 'text-primary',
          className,
        )}
        style={{ fontSize: height * 0.6, lineHeight: 1 }}
      >
        Tarawud
      </span>
    );
  }

  return (
    <img
      key={src}
      src={src}
      alt={alt}
      onError={() => setFailedSrc(src)}
      className={cn('block w-auto select-none', className)}
      style={{ height }}
      draggable={false}
      {...rest}
    />
  );
}
